import { useState } from "react";
import { Link } from "react-router-dom";
import { Menu, Search, FlaskConical, Zap } from "lucide-react";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import Sidebar from "@/components/layout/Sidebar";
import Logo from "@/components/Logo";
import { useAuth } from "@/contexts/AuthContext";
import useSettings from "@/lib/useSettings";
import NotificationBell from "@/components/NotificationBell";

export default function TopNav() {
    const { user } = useAuth();
    const settings = useSettings();
    const testMode = settings?.test_mode ?? true;
    const [open, setOpen] = useState(false);
    return (
        <header className="sticky top-0 z-30 h-16 bg-white/85 backdrop-blur border-b border-border flex items-center px-4 md:px-6 gap-3">
            <Sheet open={open} onOpenChange={setOpen}>
                <SheetTrigger asChild>
                    <button
                        className="md:hidden p-2 -ml-2 rounded-md text-gray-700 hover:bg-gray-100"
                        data-testid="mobile-menu-button"
                        aria-label="Open menu"
                    >
                        <Menu className="h-5 w-5" />
                    </button>
                </SheetTrigger>
                <SheetContent side="left" className="p-0 w-72">
                    <SheetTitle className="sr-only">Main navigation</SheetTitle>
                    <Sidebar onNavigate={() => setOpen(false)} />
                </SheetContent>
            </Sheet>

            {/* Mobile-only logo — the desktop sidebar renders the full logo
                already. On mobile the sidebar is hidden behind a Sheet, so
                without this the app has no visible branding until the user
                opens the menu. Wordmark hidden below `xs` to keep the top
                nav uncluttered. */}
            <Link
                to="/"
                className="md:hidden inline-flex items-center min-w-0"
                data-testid="topnav-mobile-logo"
                aria-label="A+ CaseFlow home"
            >
                <Logo className="h-7 w-7" showWordmark={false} />
            </Link>

            <div className="flex-1 max-w-xl relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                    type="text"
                    placeholder="Search leads, campaigns, imports…"
                    className="w-full h-9 pl-9 pr-3 text-sm bg-secondary border border-transparent focus:bg-white focus:border-border outline-none rounded-md transition-colors"
                    data-testid="global-search-input"
                />
            </div>

            <NotificationBell />
            <Link
                to="/settings"
                data-testid="topnav-mode-badge"
                title={testMode ? "Test Mode is ON — sends are mocked" : "LIVE Mode — real Twilio/SendGrid sends"}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium tracking-wide uppercase border transition-colors ${
                    testMode
                        ? "bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100"
                        : "bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-100"
                }`}
            >
                {testMode ? <FlaskConical className="h-3 w-3" /> : <Zap className="h-3 w-3" />}
                {testMode ? "Test Mode" : "Live"}
            </Link>
            <div
                className="h-8 w-8 rounded-full bg-primary/10 text-primary text-xs font-semibold flex items-center justify-center"
                data-testid="topnav-avatar"
            >
                {user?.name?.[0] ?? "A"}
            </div>
        </header>
    );
}
